import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import { getPublicIntakeLinkInfo } from "store/slices/client";
import AppColor from "utilities/AppColor";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import IntakeLinkInfoDetails from "./steps/IntakeLinkInfoDetails";
import IntakeLinkNdisDetails from "./steps/IntakeLinkNdisDetails";
import IntakeLinkSupportNeeds from "./steps/IntakeLinkSupportNeeds";
import IntakeLinkContacts from "./steps/IntakeLinkContacts";
import IntakeLinkDocuments from "./steps/IntakeLinkDocuments";
import IntakeLinkAgreementConsent from "./steps/IntakeLinkAgreementConsent";
import AppConfig from "config/AppConfig";

const { APPROVAL_STATUS } = AppConfig;

const successColor = '#1D9E75';
const useStyles = makeStyles(() => ({
  stepsContainer: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: "30px",
    minHeight: "100%",
  },
  stepItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    cursor: "pointer",
  },
  stepCircle: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: 500,
    zIndex: 1,
    position: "relative",
  },
  stepLine: {
    height: "2px",
    width: "100%",
    position: "absolute",
    top: "16px",
    left: "50%",
    zIndex: 0,
  },
  stepLabel: {
    fontSize: "11px",
    fontWeight: 500,
    marginTop: "6px",
    textAlign: "center",
  },
}));

const IntakeLinkForm = () => {
  const { token } = useParams();
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation();
  
  const [initLoading, setInitLoading] = useState(true);
  const [initError, setInitError] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [currentStep, setCurrentStep] = useState(0);
  const steps = [
    t("label.participant_details"),
    t("label.ndis_details"),
    t("label.support_needs"),
    t("label.contacts"),
    t("label.documents"),
    t("label.service_agreement_consent")
  ];

  const fetchLinkInfo = async () => {
    try {
      const res = await dispatch(getPublicIntakeLinkInfo(token));
      if (res && res.payload) {
        const detail = res.payload.client || res.payload;
        if (detail.intakeLinkExpireTime && new Date(detail.intakeLinkExpireTime) < new Date()) {
          setInitError(t("message.intake_link_expired"));
        } else {
          setClientData(detail);
          if (currentStep === 0) {
            setCurrentStep(Math.min(5, detail.intakeLinkStepCompleted || 0));
          }
        }
      }
    } catch (e) {
      setInitError(t("message.invalid_expired_intake_link"));
    }
    setInitLoading(false);
  };

  useEffect(() => {
    if (token) {
      fetchLinkInfo();
    }
  }, [token, dispatch]);

  const handleNextStep = async (isRefresh = false) => {
    if (isRefresh) {
      await fetchLinkInfo();
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleStepClick = (index) => {
    // Only allow navigating back to previous steps to update data
    const maxStep = clientData?.intakeLinkStepCompleted || 0;
    if (index <= maxStep) {
      setCurrentStep(index);
    }
  };

  const handleSuccess = () => {
    setSuccess(true);
  };

  const updateClientData = (newData) => {
    setClientData(prev => ({ ...prev, ...newData }));
  };

  if (initLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress style={{ color: AppColor.mainColor }} />
      </Box>
    );
  }

  if (initError) {
    return (
      <Container maxWidth="md" style={{ padding: "40px 0", minHeight: '100vh' }}>
        <Paper style={{ padding: "40px", textAlign: "center" }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: 60, color: AppColor.red, marginBottom: 20 }} />
          <Typography variant="h4" style={{ color: AppColor.mainColor, marginBottom: 20 }}>{t("text.link_unavailable", "Link Unavailable")}</Typography>
          <Typography variant="body1">{initError}</Typography>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="md" style={{ padding: "40px 0", minHeight: '100vh' }}>
        <Paper style={{ padding: "40px", textAlign: "center" }}>
          <i className="fas fa-check-circle" style={{ fontSize: 60, color: AppColor.green, marginBottom: 20 }} />
          <Typography variant="h4" style={{ color: AppColor.mainColor, marginBottom: 20 }}>{t("text.thank_you")}</Typography>
          <Typography variant="body1">{t("message.intake_form_submitted_successfully")}</Typography>
        </Paper>
      </Container>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <IntakeLinkInfoDetails clientData={clientData} token={token} onNextStep={handleNextStep} />;
      case 1:
        return <IntakeLinkNdisDetails clientData={clientData} token={token} onNextStep={handleNextStep} />;
      case 2:
        return <IntakeLinkSupportNeeds clientData={clientData} token={token} onNextStep={handleNextStep} updateClientData={updateClientData} />;
      case 3:
        return <IntakeLinkContacts clientData={clientData} token={token} onNextStep={handleNextStep} />;
      case 4:
        return <IntakeLinkDocuments clientData={clientData} token={token} onNextStep={handleNextStep} />;
      case 5:
        return <IntakeLinkAgreementConsent clientData={clientData} token={token} onSuccess={handleSuccess} />;
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" style={{ padding: "40px 0", minHeight: '100vh' }}>
      <Paper style={{ padding: "40px" }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" style={{ color: AppColor.mainColor, fontWeight: "bold" }}>{t("title.care_aid_support_intake_form")}</Typography>
          <Typography variant="body1" color="textSecondary" style={{ marginTop: 10 }}>{t("message.fill_out_details_below")}</Typography>
        </Box>

        <Box className={classes.stepsContainer}>
          {steps.map((label, index) => {
            const completedLimit = clientData?.intakeLinkStepCompleted || 0;
            const isCompleted = index < completedLimit;
            const isActive = index === currentStep;
            const isDoneStyle = isCompleted && !isActive;
            const isPendingStyle = !isCompleted && !isActive;
            const isNavigable = index <= completedLimit;

            return (
              <Box
                key={index}
                className={classes.stepItem}
                onClick={() => handleStepClick(index)}
                style={{ cursor: isNavigable ? "pointer" : "default" }}
              >
                <Box
                  className={classes.stepCircle}
                  style={{
                    background: isDoneStyle
                      ? successColor
                      : isActive
                      ? "#fff"
                      : "#fff",
                    color: isDoneStyle
                      ? "#fff"
                      : isActive
                      ? successColor
                      : "#888780",
                    border: isActive ? `2px solid ${successColor}` : isPendingStyle ? "2px solid #c5c4bc" : "none" }}
                >
                  {isDoneStyle ? <i className="fas fa-check" style={{ fontSize: "14px" }} /> : index + 1}
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    className={classes.stepLine}
                    style={{
                      width: "100%",
                      background: isCompleted ? successColor : "#e5e5e2",
                    }}
                  />
                )}
                {/* 
                We use width: 100% on the stepLine, and in ComplaintDetail, it also used left: 50%.
                Let's simplify it so the line connects properly.
                */}
                <Typography
                  className={classes.stepLabel}
                  style={{
                    color: isDoneStyle ? successColor : isActive ? successColor : "#888780"
                  }}
                >
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {renderStepContent()}
      </Paper>
    </Container>
  );
};

export default IntakeLinkForm;
